/**
 * Why this candidate has heard nothing: the round happened and nobody wrote it up.
 *
 * A candidate asking "any news?" is usually waiting on an interviewer rather than on the recruiter,
 * and finding that out used to mean leaving the tab for Lever. The box names the interview and the
 * people who attended it, so the next move — chase them — is on the card.
 *
 * Two things this component is careful about, both shared with FeedbackNoteBox:
 *  - It is INTERNAL. Colleagues' names and the state of an unfinished decision are not things a
 *    candidate may be told, so the box says so where the recruiter is composing a reply.
 *  - Names are rendered as TEXT. They arrive from Lever, typed by people.
 *
 * Amber, not red: nobody is at fault yet, and the tab's red is reserved for a request that has been
 * waiting too long.
 */
import { COLORS, fmtDate } from './format';
import type { InboundRequest } from '../../types/taActionBoard';

export function PendingFeedbackBox({ pending }: { pending: InboundRequest['pendingFeedback'] }) {
  if (!pending) return null;

  const round = pending.stageName ? `the ${pending.stageName}` : 'the last round';
  const held = pending.interviewAt ? fmtDate(pending.interviewAt) : null;

  return (
    <div
      className="rounded-lg p-3 text-sm"
      style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
      data-no-toggle
    >
      <p className="text-xs font-semibold">
        ⏳ Feedback is currently missing{held ? ` — ${round} was held on ${held}` : ` for ${round}`}
      </p>
      <p className="mt-1 text-xs">
        {pending.interviewers.length > 0 ? (
          <>
            Check with{' '}
            <span className="font-semibold">{pending.interviewers.join(', ')}</span> before you
            promise this candidate a decision or a date.
          </>
        ) : (
          // A panel with no named interviewer is rare but real (a booking made outside the tab).
          // Saying that beats an empty "check with" that reads like a rendering bug.
          <>
            Lever names no interviewer on that panel — open the candidate in Lever to see who
            attended.
          </>
        )}
      </p>
      <p className="mt-1 text-xs italic">
        Internal. Never tell the candidate that feedback or a colleague is outstanding.
      </p>
    </div>
  );
}
